'use client'

import type * as React from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export interface EditorToolbarButtonProps {
  children: React.ReactNode

  label: string

  shortcut?: string

  isActive?: boolean

  disabled?: boolean

  onClick: () => void

  className?: string
}

const formatShortcut = (shortcut: string): string =>
  shortcut
    .replace(
      'Mod',
      typeof navigator !== 'undefined' && navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'
    )
    .replace('Shift', '⇧')
    .replace('Alt', '⌥')
    .replace('+', '')

export const EditorToolbarButton = ({
  children,
  label,
  shortcut,
  isActive = false,
  disabled = false,
  onClick,
  className,
}: EditorToolbarButtonProps) => {
  const tooltipContent = shortcut ? `${label} (${formatShortcut(shortcut)})` : label

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          aria-pressed={isActive}
          className={cn(
            'inline-flex size-8 items-center justify-center rounded-md text-sm font-medium text-foreground transition-colors',
            'hover:bg-accent hover:text-accent-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:pointer-events-none disabled:opacity-50',
            isActive && 'bg-accent text-accent-foreground',
            className
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  )
}

EditorToolbarButton.displayName = 'EditorToolbarButton'
